import { useState, useMemo, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@material-ui/core";

import Api from "lib/api/httpRequestHelper";
import UtilsFncs from "utils/UtilsFncs";
import IntroBrandScreen from "./components/IntroBrandScreen";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import ProductList from "./components/ProductList";
import Footer from "./components/Footer";
import GridModal from "./components/GridModal";
import CartModal from "./components/CartModal";
import OrdersModal from "./components/OrdersModal";
import ConfirmModal from "./components/ConfirmModal";
import AlertModal from "./components/AlertModal";
import themeColorsDesingSystem from "assets/theme/ds-colors";

import "./styles.scss";
import {
  mapCondicoesPagamentoFromApi,
  getDefaultCondicaoPagamentoId,
  findCondicaoPagamentoById,
  getDiscountPercentFromCondicao,
  resolveLineDiscount,
  netUnitPrice,
} from "./utils/paymentConditionUtils";

// Marca numérica (API/vitrine): 1 = Karsten, 2 = Trussardi, 3 = Karsten Decor
const MARCA_NUM_TO_BRAND_KEY = {
  1: "karsten",
  2: "trussardi",
  3: "karsten_decor",
};

function getVitrineKey(vitrine) {
  return String(vitrine?.IdVitrineVirtualB2b ?? "");
}

function formatVitrineVigenciaLabel(vitrine) {
  const inicio =
    vitrine?.DataVigenciaInicioFormat ||
    vitrine?.dataVigenciaInicioFormat ||
    "";
  const fim =
    vitrine?.DataVigenciaFimFormat || vitrine?.dataVigenciaFimFormat || "";
  if (inicio && fim) return `${inicio} a ${fim}`;
  return inicio || fim || "";
}

/**
 * PDP B2B — agrupamento de cards:
 * - A chave vem do List PDP em `FamilyKey` (sem tamanho na família: mesma coleção/campanha + mesmo tipo/linha).
 * - Tamanhos e cores ficam na grade (matriz cor × tamanho) e no carrossel do card (seletor de tamanho quando há mais de um).
 */
function getSizeLabelFromProduct(product) {
  const fromApi = (product.Tamanho || "").trim();
  if (fromApi) return fromApi;
  const specs = product.Especificacoes || [];
  const tamExact = specs.find((s) =>
    /^TAMANHO$/i.test(String(s.DescricaoEspecificacao || "").trim()),
  );
  if (tamExact) {
    const v = `${tamExact.Valor1 || ""} ${tamExact.Valor2 || ""}`.trim();
    if (v) return v;
  }
  const sizeSpec = specs.find((s) =>
    /tamanho|medida|dimens|tam\b/i.test(String(s.DescricaoEspecificacao || "")),
  );
  if (sizeSpec) {
    const v = `${sizeSpec.Valor1 || ""} ${sizeSpec.Valor2 || ""}`.trim();
    if (v) return v;
  }
  return "";
}

function getCorSpecFromProduct(product) {
  const specs = product.Especificacoes || [];
  return specs.find((s) =>
    /^cor$/i.test(String(s.DescricaoEspecificacao || "").trim()),
  );
}

/**
 * Rótulo de cor para grade / carrinho — alinhado ao List PDP (EDI/NomeCor primeiro, depois spec COR).
 * Inverter para spec primeiro fazia muitos SKUs herdarem o mesmo texto de COR genérico (ex. BRANCO) e sumiam as demais cores.
 */
function getCorFromApiProduct(product) {
  const corApi = (product.Cor || "").trim();
  if (corApi) return corApi;

  const spec = getCorSpecFromProduct(product);
  if (spec) {
    const v1 = (spec.Valor1 || "").trim();
    const v2 = (spec.Valor2 || "").trim();
    if (v1 || v2) return `${v1} ${v2}`.trim();
  }
  const fc = product.FotosPorCor?.[0]?.NomeCor;
  if (fc && String(fc).trim()) {
    const t = String(fc).trim();
    if (t !== "Geral") return t;
  }
  return "Geral";
}

function stripTokenFromEnd(name, token) {
  if (!name || !token) return name;
  try {
    const escaped = String(token).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return name.replace(new RegExp(`\\s+${escaped}\\s*$`, "i"), "").trim();
  } catch {
    return name;
  }
}

function collapseSpaces(s) {
  return String(s || "")
    .trim()
    .replace(/\s+/g, " ");
}

/** Remove token como palavra/frase inteira (alinha ao BuildNomeTituloGrade no backend). */
function stripWholeWordPhrase(name, token) {
  if (!name || !token) return name;
  const parts = String(token).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return name;
  const escaped = parts.map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  try {
    const pattern =
      parts.length === 1
        ? new RegExp(`\\b${escaped[0]}\\b`, "gi")
        : new RegExp(`\\b${escaped.join("\\s+")}\\b`, "gi");
    return collapseSpaces(name.replace(pattern, " "));
  } catch {
    return name;
  }
}

/** Cor no ERP costuma ser frase ("Azul Marinho"); o nome pode terminar só em "Marinho". */
function removeCorFromTitle(name, cor) {
  let n = name;
  if (!cor || cor === "Geral") return collapseSpaces(n);
  const full = String(cor).trim();
  n = stripWholeWordPhrase(n, full);
  n = stripTokenFromEnd(n, full);
  const words = [
    ...new Set(full.split(/\s+/).filter((w) => w.length >= 2)),
  ].sort((a, b) => b.length - a.length);
  for (const w of words) {
    n = stripWholeWordPhrase(n, w);
    n = stripTokenFromEnd(n, w);
  }
  return collapseSpaces(n);
}

/** Remove cor do título usando o mesmo rótulo que a grade (getCorFromApiProduct), mais fragmentos da spec se existirem. */
function removeCorFromTitlePreferSpec(name, product) {
  const primary = getCorFromApiProduct(product);
  let n = removeCorFromTitle(name, primary);
  const spec = getCorSpecFromProduct(product);
  const v1 = (spec?.Valor1 || "").trim();
  const v2 = (spec?.Valor2 || "").trim();
  if (v1 || v2) {
    const cands = [...new Set([`${v1} ${v2}`.trim(), v1, v2].filter(Boolean))];
    for (const c of cands) {
      if (c && primary && c.toUpperCase() === primary.toUpperCase()) continue;
      n = removeCorFromTitle(n, c);
    }
  }
  return collapseSpaces(n);
}

/**
 * Nome/Desc/Linha sem cor — para match de linha comercial no card (extractCommercialLineFromProduct).
 */
function getProductSnapshotForFamilyLineMatch(product) {
  const cor = getCorFromApiProduct(product);
  let nome = (product.Nome || "").trim();
  let desc = (product.Descricao || "").trim();
  let linha = (product.Linha || "").trim();
  if (cor && cor !== "Geral") {
    nome = removeCorFromTitlePreferSpec(nome, product);
    desc = removeCorFromTitlePreferSpec(desc, product);
    linha = removeCorFromTitle(linha, cor);
  }
  return { nome, desc, linha };
}

function looksLikeErpNomeAbreviado(s) {
  if (!s || typeof s !== "string") return false;
  const t = s.trim();
  if (t.length > 96) return false;
  if (/^\s*JG\s+(COLCHA|CAMA)\b/i.test(t)) return true;
  if (/\bC\d{2,4}F\b/i.test(t)) return true;
  return false;
}

/** Jg. B/r …, código - 01/80209 — texto WMW que não deve ir direto ao card. */
function looksLikeAbbreviatedCommercialProductTitle(s) {
  if (!s || typeof s !== "string") return false;
  const t = s.trim();
  if (looksLikeErpNomeAbreviado(t)) return true;
  if (/^\s*JG\.?\s*B\/?R\b/i.test(t)) return true;
  if (/^\s*JG\.?\s*B\/C\b/i.test(t)) return true;
  if (/^\s*JG\s+\d/i.test(t)) return true;
  if (/\b\d+\s*PC\b/i.test(t) && t.length < 80) return true;
  if (/^\s*JG\.\s+|^\s*JG\s+/i.test(t) && t.length < 80) return true;
  return false;
}

/** Coleção/marketing só (ex.: "Exclusivo - BF 2025") sem nome de produto. */
function isGenericCampaignProductTitle(s) {
  const t = (s || "").trim().toLowerCase();
  if (!t) return true;
  if (/^exclusivo\b/.test(t)) return true;
  if (
    /^(janeiro|fevereiro|março|marco|abril|maio|junho|julho|agosto|setembro|outubro|novembro|dezembro)\s+\d{2,4}\s*[•.]?\s*$/i.test(
      String(s || "").trim(),
    )
  )
    return true;
  if (/oportunidade\s+fora\s+cole/i.test(t)) return true;
  return false;
}

function isGoodEnoughRetailProductTitle(s) {
  const t = (s || "").trim();
  if (t.length < 8) return false;
  if (isGenericCampaignProductTitle(t)) return false;
  if (looksLikeAbbreviatedCommercialProductTitle(t)) return false;
  return true;
}

function findProductSpecByLabelRegex(product, labelRe) {
  const specs = product.Especificacoes || [];
  return specs.find((e) =>
    labelRe.test(String(e.DescricaoEspecificacao || "").trim()),
  );
}

function productSpecCombinedText(espec) {
  if (!espec) return "";
  return collapseSpaces(`${espec.Valor1 || ""} ${espec.Valor2 || ""}`.trim());
}

/** Texto corrido da spec DESCRIÇÃO PRODUTO (HTML removido) para parsing do nome da linha. */
function getDescricaoProdutoSpecBlob(product, maxLen = 420) {
  const espec = findProductSpecByLabelRegex(
    product,
    /^DESCRI[ÇC]ÃO\s+PRODUTO$/i,
  );
  const raw = productSpecCombinedText(espec);
  if (!raw) return "";
  const noHtml = String(raw).replace(/<[^>]*>/gi, " ");
  const oneLine = collapseSpaces(noHtml);
  return oneLine.length > maxLen
    ? oneLine.substring(0, maxLen).trim()
    : oneLine.trim();
}

function normalizeQuantidadePecasLabel(text) {
  const t = collapseSpaces(text || "").toLowerCase();
  let m = t.match(/(\d+)\s*pe[çc]as?/);
  if (m) return `${m[1]} peças`;
  m = t.match(/^(\d+)\s*$/);
  if (m) return `${m[1]} peça${m[1] === "1" ? "" : "s"}`;
  return "";
}

/** Última palavra “marca” no nome técnico (ex.: … MAGNA - 01/80209). */
function extractLineTokenFromAbbreviatedNome(nome) {
  const s = String(nome || "")
    .trim()
    .replace(/\s*-\s*\d{1,4}(?:\/\d+)?.*$/i, "")
    .replace(/\s+\d\s*$/i, "");
  const parts = s.split(/\s+/).filter(Boolean);
  const stop = new Set([
    "jg",
    "jg.",
    "b/r",
    "b/r.",
    "5pc",
    "4pc",
    "3pc",
    "colcha",
    "cama",
    "sol",
    "sor",
  ]);
  const token =
    [...parts].reverse().find((p) => {
      const clean = /^[A-Za-zÀ-ÖØ-öø-ÿ\d]+$/;
      const u = p.replace(/\./g, "");
      return (
        u.length >= 3 &&
        clean.test(u.replace(/\d+$/, "") || "") &&
        !stop.has(u.toLowerCase()) &&
        !/^\d+pc$/i.test(u)
      );
    }) || "";
  if (!token) return "";
  const low = token.toLowerCase();
  return low.charAt(0).toUpperCase() + low.slice(1);
}

/** Linha (Magna, Saxonia…) a partir do texto institucional. */
function extractLineNameFromDescriptionBlob(blob) {
  if (!blob) return "";
  const t = String(blob);
  let m = t.match(/\bjogo\s+de\s+toalhas\s+([A-Za-zÀ-ÿÁÉÍÓÚÂÃÊÕÇ]+)/i);
  if (m) return m[1];
  m = t.match(/\bcom\s+a\s+[Tt]oalha\s+([A-Za-zÀ-ÿÁÉÍÓÚÂÃÊÕÇ]+)\b/i);
  if (m) return m[1];
  m = t.match(/\b[AaÀà]\s+t[oô]alhas?\s+([A-Za-zÀ-ÿÁÉÍÓÚÂÃÊÕÇ]+)\b/);
  if (m) return m[1];
  m = t.match(/\b[AaÀà]\s+[Tt]oalha\s+([A-Za-zÀ-ÿÁÉÍÓÚÂÃÊÕÇ]+)\s+tem\b/i);
  if (m) return m[1];
  m = t.match(/\b[AaÀà]\s+[Tt]oalha\s+([A-Za-zÀ-ÿÁÉÍÓÚÂÃÊÕÇ]+)\s+unas?\b/i);
  if (m) return m[1];
  m = t.match(/\b[AaÀà]\s+[Tt]oalha\s+([A-Za-zÀ-ÿÁÉÍÓÚÂÃÊÕÇ]+)\s+combin/i);
  if (m) return m[1];
  m = t.match(/\b[aà]\s+toalha\s+([A-Za-zÀ-ÿÁÉÍÓÚÂÃÊÕÇ]+)\s+surpreend/i);
  if (m) return m[1];
  return "";
}

const banhaoRe = /banh[aã]o|banhao/i;

/** “Jogo de banho”; “Toalha de banho”, etc., a partir de categoria/subcategoria/specs. */
function inferReadableBanhoTipoLabel(product) {
  const sub = String(product.Subcategoria || "").trim();
  const cat = String(product.Categoria || "").trim();
  const itensSpec = productSpecCombinedText(
    findProductSpecByLabelRegex(product, /^ITENS\s+INCLUSOS$/i),
  );
  const itLower = itensSpec.toLowerCase();
  const tamNorm = String(getSizeLabelFromProduct(product) || "").trim();

  if (
    /jogo\s+de\s+banho/i.test(sub) ||
    (/\bjogo\b/i.test(sub) && /\bbanho\b/i.test(cat.toLowerCase())) ||
    (/\bjogo\b/i.test(itLower) && /\btoalhas?\b/i.test(itLower))
  )
    return { prefix: "Jogo de banho", isJogo: true };

  if (
    /\b\d\s+toalhas?\b[\s\S]*;/i.test(itLower) ||
    /;\s*\d\s+toalhas?\b/i.test(itLower)
  )
    return { prefix: "Jogo de banho", isJogo: true };

  if (
    /^t[oô]alha\s+de\s+rosto\b/i.test(sub) ||
    /rosto/i.test(cat.toUpperCase()) ||
    /\b(rosto)\b/i.test(tamNorm.toLowerCase()) ||
    /\btoalha\s+de\s+rosto\b/i.test(itLower)
  )
    return { prefix: "Toalha de rosto", isJogo: false };

  if (/piso\b/i.test(tamNorm) || /\bpiso\b/i.test(itLower))
    return { prefix: "Toalha de piso", isJogo: false };

  if (
    banhaoRe.test(`${tamNorm} ${itLower}`) ||
    /^\s*toalhas?\s+[Bb]anh[aã]o\b/i.test(itLower) ||
    /toalha\s+banh[aã]o\b/i.test(itLower)
  )
    return { prefix: "Toalha de banhão", isJogo: false };

  if (
    /^\s*banho\s*$/i.test(tamNorm) ||
    /\btoalha\s+de\s+banho\b(?!\s+[a-z])/i.test(itLower)
  )
    return { prefix: "Toalha de banho", isJogo: false };

  const subLc = sub.toLowerCase();
  const catLc = cat.toLowerCase();
  const looksBanhoDept =
    catLc === "banho" || subLc.includes("banho") || subLc.includes("toalha");

  if (looksBanhoDept && sub && catLc !== "produto")
    return { prefix: formatTituloGradeExibicao(sub) || sub, isJogo: false };

  if (looksBanhoDept) return { prefix: "Toalha de banho", isJogo: false };

  return { prefix: "", isJogo: false };
}

function buildSpecificationBasedDisplayTitle(product) {
  const descBlob = getDescricaoProdutoSpecBlob(product);
  const lineFromDescOrErp =
    extractLineNameFromDescriptionBlob(descBlob) ||
    extractLineTokenFromAbbreviatedNome(
      product.NomeTituloGrade || product.Nome || "",
    );

  const qtySpecTxt = productSpecCombinedText(
    findProductSpecByLabelRegex(product, /^QUANTIDADE\s+DE\s+PE[ÇC]AS$/i),
  );
  const qtyNormalized = normalizeQuantidadePecasLabel(qtySpecTxt);

  const catU = String(product.Categoria || "")
    .trim()
    .toUpperCase();
  const sub = String(product.Subcategoria || "")
    .trim()
    .toUpperCase();

  const isTowelBanhoContext =
    catU === "BANHO" ||
    catU.includes("TOALHA") ||
    sub.includes("BANHO") ||
    sub.includes("TOALHA");

  let joined = "";

  if (isTowelBanhoContext) {
    const inf = inferReadableBanhoTipoLabel(product);
    let parts = [];
    if (inf.isJogo)
      parts = [inf.prefix, lineFromDescOrErp, qtyNormalized].filter(Boolean);
    else parts = [inf.prefix, lineFromDescOrErp].filter(Boolean);
    joined = collapseSpaces(parts.join(" "));
    if (/^to\s/i.test(joined))
      joined = collapseSpaces(joined.replace(/^to\s+/i, "Toalha "));
    if (/^banho\b/i.test(joined) && joined.length < 20)
      joined = collapseSpaces(joined.replace(/^banho\b/i, "Toalha de banho"));
  }

  if (
    lineFromDescOrErp &&
    joined &&
    !new RegExp(
      `\\b${lineFromDescOrErp.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
      "i",
    ).test(joined)
  )
    joined = collapseSpaces(`${joined} ${lineFromDescOrErp}`);
  else if (!joined && lineFromDescOrErp && qtyNormalized)
    joined = collapseSpaces(`${lineFromDescOrErp} ${qtyNormalized}`);
  else if (!joined && lineFromDescOrErp) joined = lineFromDescOrErp;
  else if (!joined && qtyNormalized) joined = qtyNormalized;

  /** Fallback leve para categorias não “banho” listadas mas com descrição rica */
  if (!joined && descBlob.length > 20) {
    const lineOnly = extractLineNameFromDescriptionBlob(descBlob);
    if (lineOnly) joined = lineOnly;
  }

  if (!joined) {
    const vis = productSpecCombinedText(
      findProductSpecByLabelRegex(product, /^DESCRI[ÇC]ÃO\s+VISUAL$/i),
    );
    const shortVis = collapseSpaces(
      String(vis)
        .replace(/[.;].*$/, "")
        .trim(),
    );
    if (shortVis.length > 3 && shortVis.length < 90) joined = shortVis;
  }

  if (!joined) return "";

  joined = sanitizeSpecBuiltBanhoTitle(joined) || "";

  joined = collapseSpaces(joined).replace(/^to\s+/i, "Toalha ");
  return formatTituloGradeExibicao(joined) || joined;
}

/** Sem “Toalha” só, sem tipo/linha. */
function sanitizeSpecBuiltBanhoTitle(s) {
  const t = (s || "").trim();
  return t.toLowerCase() === "toalha" ? "" : s || "";
}

/** Nome curto no ERP (JG COLCHA…); descrição literal (API Descricao) com texto vendável. */
function resolveNomeBaseParaTituloGrade(product) {
  const curto = (product.Nome || "").trim();
  const longo = (product.Descricao || "").trim();
  if (
    longo &&
    (looksLikeErpNomeAbreviado(curto) ||
      looksLikeAbbreviatedCommercialProductTitle(curto)) &&
    longo.length >= curto.length
  ) {
    return longo;
  }
  if (curto) return curto;
  return longo;
}

/** Alinha ao StripTechnicalNomeComercialNoise no List PDP (ERP: COR 1, TAMANHOEMCM, COMPOSICAO…). */
function stripTechnicalNomeGradeNoise(text) {
  if (!text) return text;
  let s = text;
  s = s.replace(/\s+COR\s+\d+.*$/i, "");
  s = s.replace(/\s+TAMANHOEMCM\s+.*$/i, "");
  s = s.replace(/\s+TAMEMCM\s+.*$/i, "");
  s = s.replace(/\s+TAMANHO\s*EM\s*CM\s+.*$/i, "");
  s = s.replace(/\s+RETANGULAR\s+.*$/i, "");
  s = s.replace(/\s+COMPOSICAO\s+.*$/i, "");
  s = s.replace(/\s+COMPOSIÇÃO\s+.*$/i, "");
  s = s.replace(/\s+1a\s+QUALIDADE\s+.*$/i, "");
  s = s.replace(/\s+1ª\s+QUALIDADE\s+.*$/i, "");
  s = s.replace(/\s+2a\s+QUALIDADE\s+.*$/i, "");
  s = s.replace(/\s+2ª\s+QUALIDADE\s+.*$/i, "");
  s = s.replace(/\s+\d+\s*G\/M2\s*$/i, "");
  return collapseSpaces(s);
}

/** Título legível (pt): artigos curtos em minúsculo; "Peças" com ç. */
function formatTituloGradeExibicao(s) {
  if (!s) return s;
  const lower = s.toLowerCase().trim();
  const small = new Set([
    "de",
    "da",
    "do",
    "das",
    "dos",
    "e",
    "em",
    "com",
    "para",
    "por",
    "a",
    "o",
    "as",
    "os",
  ]);
  const words = lower.split(/\s+/).filter(Boolean);
  const titled = words.map((w, i) => {
    if (i > 0 && small.has(w)) return w;
    return w.charAt(0).toUpperCase() + w.slice(1);
  });
  let out = titled.join(" ");
  out = out.replace(/\bPecas\b/gi, "Peças");
  return out;
}

/**
 * Segmento da toalha (banho, rosto, piso, academia…) vem do List PDP em `SegmentoToalha` — resolvido na API.
 */
function getToalhaTipoLabelFromProduct(product) {
  const s = (product.SegmentoToalha ?? product.segmentoToalha ?? "")
    .trim()
    .toLowerCase();
  return s || "";
}

/**
 * Ex.: “Toalha de 100% Algodão Nuvia” + SegmentoToalha banho → “Toalha de banho Nuvia”.
 * Evita card só “Toalha de (linha/coleção)” quando falta o tipo no texto já limpo.
 */
function enrichToalhaDisplayTitle(product, title) {
  const tipo = getToalhaTipoLabelFromProduct(product);
  if (!tipo) return title;
  const t = (title || "").trim();
  if (!/\btoalha\b/i.test(t)) return title;
  if (new RegExp(`\\b${tipo}\\b`, "i").test(t)) return title;
  let rest = t.replace(/^toalha\s+de\s+/i, "").trim();
  rest = rest.replace(/^100%\s*algod[aã]o\s+/i, "").trim();
  rest = stripTechnicalNomeGradeNoise(rest);
  if (!rest && (product.Linha || "").trim()) {
    rest = (product.Linha || "").trim();
  }
  const linhaTrim = (product.Linha || "").trim();
  if (
    linhaTrim &&
    /^(JANEIRO|FEVEREIRO|MAR[ÇC]O|ABRIL|MAIO|JUNHO|JULHO|AGOSTO|SETEMBRO|OUTUBRO|NOVEMBRO|DEZEMBRO)\s+\d{2,4}$/i.test(
      rest,
    )
  ) {
    rest = linhaTrim;
  }
  if (!rest) return t;
  const merged = `Toalha de ${tipo} ${rest}`.trim();
  return formatTituloGradeExibicao(merged);
}

function deriveDisplayProductTitleFromApiProduct(product) {
  let tituloGrade = (product.NomeTituloGrade || "").trim();
  if (tituloGrade && looksLikeAbbreviatedCommercialProductTitle(tituloGrade)) {
    tituloGrade = "";
  }

  let out = "";
  if (tituloGrade) {
    out = formatTituloGradeExibicao(stripTechnicalNomeGradeNoise(tituloGrade));
  } else {
    let nome = resolveNomeBaseParaTituloGrade(product);
    if (!nome) nome = (product.Nome || product.Descricao || "").trim();
    if (!nome) {
      const specOnly =
        sanitizeSpecBuiltBanhoTitle(
          buildSpecificationBasedDisplayTitle(product),
        ) || "";
      return enrichToalhaDisplayTitle(product, specOnly || "");
    }
    const tam = getSizeLabelFromProduct(product);
    if (tam) {
      nome = stripWholeWordPhrase(nome, tam);
      nome = stripTokenFromEnd(nome, tam);
    }
    nome = removeCorFromTitlePreferSpec(nome, product);
    nome = collapseSpaces(nome) || (product.Nome || "").trim();
    out = formatTituloGradeExibicao(stripTechnicalNomeGradeNoise(nome)) || nome;
  }

  const specBuilt = sanitizeSpecBuiltBanhoTitle(
    buildSpecificationBasedDisplayTitle(product),
  );

  if (specBuilt && (!out || !isGoodEnoughRetailProductTitle(out)))
    out = specBuilt || out;

  return enrichToalhaDisplayTitle(product, out || "");
}

function consensusProductDisplayTitle(titles) {
  const cleaned = titles.map((n) => (n || "").trim()).filter(Boolean);
  if (cleaned.length === 0) return "";
  const uniq = [...new Set(cleaned)];
  if (uniq.length === 1) return uniq[0];
  // Variantes só diferem por cor/tamanho no fim — o prefixo comum virava só "Colcha"; usar o título mais completo.
  return cleaned.reduce(
    (best, t) => (t.length > best.length ? t : best),
    cleaned[0],
  );
}

function sortSizesNatural(sizes) {
  const order = [
    "PP",
    "P",
    "M",
    "G",
    "GG",
    "XG",
    "EG",
    "SOLTEIRO",
    "CASAL",
    "QUEEN",
    "KING",
    "BABY",
  ];
  return [...sizes].sort((a, b) => {
    const ua = String(a).toUpperCase().trim();
    const ub = String(b).toUpperCase().trim();
    const ia = order.findIndex((o) => ua === o || ua.includes(o));
    const ib = order.findIndex((o) => ub === o || ub.includes(o));
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return ua.localeCompare(ub, "pt-BR");
  });
}

function pickConsensusFamiliaField(rows, field) {
  const vals = [
    ...new Set(rows.map((r) => String(r[field] || "").trim()).filter(Boolean)),
  ];
  if (vals.length === 1) return vals[0];
  return vals[0] || "";
}

/** Shape mínimo para inferência de tipo/linha a partir das linhas já enriquecidas da família. */
const TOWEL_USE_NOT_LINE = new Set([
  "rosto",
  "banho",
  "banhao",
  "banhão",
  "piso",
  "lavabo",
  "visita",
  "mão",
  "mao",
  "banho e rosto",
]);

function sanitizeProductLineCandidate(s) {
  const t = collapseSpaces(String(s || ""));
  if (!t || t.length < 3) return "";
  const key = t
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  if (TOWEL_USE_NOT_LINE.has(key)) return "";
  return t;
}

/** Título que só indica o uso (rosto/banho), sem nome de linha — injusto quando a grade tem vários usos. */
function towelTitleIsUseOnly(title) {
  const t = collapseSpaces(String(title || "")).trim();
  return /^toalha\s+(de\s+)?(rosto|banho|banh(a|ão|ao))\s*$/i.test(t);
}

function rowTowelUseBucket(row) {
  const rawSeg = String(row.segmentoToalha || "")
    .trim()
    .toLowerCase();
  if (rawSeg.includes("rosto")) return "rosto";
  if (rawSeg.includes("banhão") || rawSeg.includes("banhao")) return "banhao";
  if (rawSeg.includes("banho")) return "banho";
  if (rawSeg.includes("piso")) return "piso";

  const w = famRowToApiProduct(row);
  const inf = inferReadableBanhoTipoLabel(w);
  if (inf.isJogo) return "jogo";
  const p = String(inf.prefix || "").toLowerCase();
  if (p.includes("rosto")) return "rosto";
  if (p.includes("banhão") || p.includes("banhao")) return "banhao";
  if (p.includes("banho")) return "banho";
  if (p.includes("piso")) return "piso";

  const tl = String(row.tamanhoLabel || "")
    .trim()
    .toLowerCase();
  if (tl.includes("rosto")) return "rosto";
  if (tl.includes("banhão") || tl.includes("banhao")) return "banhao";
  if (tl.includes("banho")) return "banho";
  if (tl.includes("piso")) return "piso";
  return "";
}

/** Mesma família com rosto + banho (ou mais), por segmento API, subcategoria ou coluna TAMANHO. */
function familyAggregatesMultipleTowelUses(products) {
  const buckets = new Set();
  for (const row of products) {
    const b = rowTowelUseBucket(row);
    if (b && b !== "jogo") buckets.add(b);
  }
  return buckets.size >= 2;
}

function scrubSubtituloTowelUseArtifacts(s) {
  let x = String(s || "").trim();
  let prev;
  do {
    prev = x;
    x = x.replace(
      /\s*•\s*(Rosto|Banho|Banh[aã]o|Banhao|Piso|Lavabo)\s*$/gi,
      "",
    );
  } while (x !== prev);
  return collapseSpaces(x.replace(/\s*•\s*$/g, "").trim());
}

function famRowToApiProduct(row) {
  return {
    Categoria: row.categoriaApi || "",
    Subcategoria: row.subcategoriaApi || "",
    Especificacoes: row.especificacoes || [],
    Tamanho: row.tamanhoLabel || "",
    Nome: row.fullName || "",
    NomeTituloGrade: row.displayTitle || "",
    Descricao: "",
    SegmentoToalha: row.segmentoToalha || "",
    Linha: "",
  };
}

function extractStyleLineFromFamilyRows(products) {
  for (const row of products) {
    const w = famRowToApiProduct(row);
    const blob = getDescricaoProdutoSpecBlob(w);
    const fromDesc = extractLineNameFromDescriptionBlob(blob);
    if (fromDesc) {
      const s = sanitizeProductLineCandidate(collapseSpaces(fromDesc));
      if (s) return s;
    }
  }
  for (const row of products) {
    const fromAbbrev = extractLineTokenFromAbbreviatedNome(
      `${row.fullName || ""} ${row.displayTitle || ""}`,
    );
    if (fromAbbrev) {
      const s = sanitizeProductLineCandidate(collapseSpaces(fromAbbrev));
      if (s) return s;
    }
  }
  return "";
}

function familyHasBanhoTowelJogo(products) {
  return products.some(
    (row) => inferReadableBanhoTipoLabel(famRowToApiProduct(row)).isJogo,
  );
}

/**
 * Família com mais de um uso (rosto/banho…) na grade: título neutro "Toalha {linha}" ou "Toalha".
 */
function deriveFallbackNeutralTowelFamilyTitle(products, fam) {
  if (!familyAggregatesMultipleTowelUses(products)) return "";
  if (familyHasBanhoTowelJogo(products)) return "";
  const rawLine =
    extractStyleLineFromFamilyRows(products) ||
    (fam?.commercialLineName ? String(fam.commercialLineName).trim() : "") ||
    (fam?.lineName ? String(fam.lineName).trim() : "");
  const line = sanitizeProductLineCandidate(collapseSpaces(rawLine));
  if (!line) return "Toalha";
  const merged = collapseSpaces(`Toalha ${line}`);
  return formatTituloGradeExibicao(merged) || merged;
}

function finalizeFamilyRecord(fam) {
  const products = fam.products || [];
  if (products.length === 0) return;
  const titles = products.map((p) => p.displayTitle).filter(Boolean);
  const apiFamTitle = pickConsensusFamiliaField(
    products,
    "tituloFamiliaCardPdp",
  );
  const consensusTitle = consensusProductDisplayTitle(titles);
  const multiUse = familyAggregatesMultipleTowelUses(products);

  fam.subtituloCardPdp = scrubSubtituloTowelUseArtifacts(
    pickConsensusFamiliaField(products, "subtituloCardPdp"),
  );

  const neutralFamTitle = deriveFallbackNeutralTowelFamilyTitle(products, fam);

  let displayName = (apiFamTitle || "").trim();
  if (multiUse) {
    if (
      towelTitleIsUseOnly(displayName) ||
      (!displayName && towelTitleIsUseOnly(consensusTitle))
    ) {
      displayName = neutralFamTitle;
    } else if (!displayName) {
      displayName =
        neutralFamTitle ||
        (towelTitleIsUseOnly(consensusTitle)
          ? neutralFamTitle
          : consensusTitle);
    }
  } else {
    displayName = displayName || neutralFamTitle || consensusTitle;
  }

  fam.productDisplayName = (
    displayName ||
    consensusTitle ||
    fam.name ||
    ""
  ).trim();

  const lineHintRaw =
    (fam.commercialLineName || "").trim() ||
    extractStyleLineFromFamilyRows(products) ||
    (fam.lineName || "").trim();
  const lineHint = sanitizeProductLineCandidate(lineHintRaw);
  if (lineHint) {
    const sub = String(fam.subtituloCardPdp || "").trim();
    const subLc = sub.toLowerCase();
    const hasLine = subLc.includes(lineHint.toLowerCase());
    if (sub && !hasLine) {
      fam.subtituloCardPdp = `${sub} • ${lineHint}`;
    } else if (!sub) {
      fam.subtituloCardPdp = [fam.collectionName, fam.colecaoSerie, lineHint]
        .map((x) => String(x || "").trim())
        .filter(Boolean)
        .join(" • ");
    }
  }
  fam.subtituloCardPdp = scrubSubtituloTowelUseArtifacts(
    fam.subtituloCardPdp || "",
  );
  const col = (fam.collectionName || "").trim();
  const nc = (fam.nomeColecao || "").trim();
  const line =
    (fam.commercialLineName || "").trim() || (fam.lineName || "").trim();
  const serie = (fam.colecaoSerie || "").trim();
  fam.name =
    fam.productDisplayName ||
    [line, serie].filter(Boolean).join(" · ") ||
    [line, nc].filter(Boolean).join(" · ") ||
    col ||
    fam.commercialName ||
    "Produtos";

  const colors = [
    ...new Set(
      products.map((p) => p.corLabel).filter((c) => c && c !== "Geral"),
    ),
  ].sort((a, b) => a.localeCompare(b, "pt-BR"));
  const sizes = [
    ...new Set(products.map((p) => p.tamanhoLabel).filter(Boolean)),
  ];
  fam.matrixSizes = sortSizesNatural(sizes);
  fam.matrixColors = colors;
  fam.productByColorSize = {};
  products.forEach((p) => {
    const c = p.corLabel || "Geral";
    const t = p.tamanhoLabel || "";
    const k = `${c}|||${t}`;
    if (!fam.productByColorSize[k]) fam.productByColorSize[k] = p;
  });
  fam.matrixMode =
    fam.matrixSizes.length > 0 && fam.matrixColors.length > 0
      ? "colorSize"
      : "legacy";
}

function mergeFotosPorCor(existing, product) {
  const list = existing || [];
  const incoming = product.FotosPorCor || [];
  const byName = new Map();
  list.forEach((c) => {
    const n = c.NomeCor || "Geral";
    byName.set(n, {
      NomeCor: n,
      Urls: c.Urls && Array.isArray(c.Urls) ? [...c.Urls] : [],
    });
  });

  const colorKey = getCorFromApiProduct(product);
  const corHexApi = (product.CorHex || "").trim();
  const urlSet = [];
  incoming.forEach((c) => {
    const urls = c.Urls && Array.isArray(c.Urls) ? c.Urls : [];
    urlSet.push(...urls);
  });
  (product.Fotos || []).forEach((u) => u && urlSet.push(u));
  const urls = [...new Set(urlSet.filter(Boolean))];

  if (!byName.has(colorKey)) {
    byName.set(colorKey, {
      NomeCor: colorKey,
      Urls: [...urls],
      CorHex: corHexApi || undefined,
    });
  } else {
    const prev = byName.get(colorKey);
    const merged = [
      ...new Set([...(prev.Urls || []), ...urls].filter(Boolean)),
    ];
    const mergedHex = corHexApi || prev.CorHex || "";
    byName.set(colorKey, {
      ...prev,
      NomeCor: colorKey,
      Urls: merged,
      CorHex: mergedHex || undefined,
    });
  }

  return Array.from(byName.values());
}

function rebuildFamilyColorFields(fam) {
  const merged = fam.fotosPorCorMerged || [];
  if (merged.length === 0) {
    fam.variants = ["Geral"];
    fam.variantImages = {};
    fam.variantColorHex = {};
    return;
  }
  fam.variants = merged.map((c) => c.NomeCor || "Geral");
  const urlsByVariant = {};
  const hexByVariant = {};
  merged.forEach((c) => {
    const name = c.NomeCor || "Geral";
    urlsByVariant[name] = c.Urls && Array.isArray(c.Urls) ? c.Urls : [];
    if (c.CorHex) hexByVariant[name] = c.CorHex;
  });
  fam.variantImages = urlsByVariant;
  fam.variantColorHex = hexByVariant;
}

function resolveFamilyKeyFromApiProduct(product, idProdutoFallback) {
  const k = String(product?.FamilyKey ?? product?.familyKey ?? "").trim();
  return (
    k || `legacy_sku_${idProdutoFallback ?? product?.IdProdutoString ?? "x"}`
  );
}

/** Texto da especificação "COLEÇÃO" (ex.: Ser Casa - Janeiro 2026) — diferente de NomeColecao mensal. */
function getColecaoSerieFromSpecs(product) {
  const specs = product.Especificacoes || [];
  const c = specs.find((s) =>
    /^COLEÇÃO$/i.test(String(s.DescricaoEspecificacao || "").trim()),
  );
  if (!c) return "";
  return `${c.Valor1 || ""} ${c.Valor2 || ""}`.trim();
}

/**
 * Nomes em ProdutoLinha que não devem definir “linha comercial” no cabeçalho do card.
 */
const LINHA_SUBSTRING_DENYLIST = new Set([
  "QUEEN",
  "KING",
  "SOLTEIRO",
  "CASAL",
  "QUEEN SIZE",
  "KING SIZE",
  "SUPER KING",
  "SINGLE",
  "FULL",
  "TWIN",
  "DOUBLE",
  "STANDARD",
  "PP",
  "P",
  "M",
  "G",
  "GG",
  "XG",
  "EG",
  "BABY",
  "BERÇO",
  "BERCO",
  "INFANTIL",
]);

function filterNomesLinhasComerciaisParaAgrupamento(names) {
  return names.filter((n) => {
    const t = String(n || "").trim();
    if (t.length < 3) return false;
    if (LINHA_SUBSTRING_DENYLIST.has(t.toUpperCase())) return false;
    return true;
  });
}

/** Lista do List PDP (ProdutoLinha); backend já envia do mais longo ao mais curto. */
function normalizeNomesLinhasComerciais(apiResult) {
  const raw =
    apiResult?.NomesLinhasComerciais ?? apiResult?.nomesLinhasComerciais ?? [];
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const s of raw) {
    const t = String(s || "").trim();
    if (!t) continue;
    const key = t.toUpperCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  const filtered = filterNomesLinhasComerciaisParaAgrupamento(out);
  return filtered.sort((a, b) => b.length - a.length);
}

function extractCommercialLineFromProduct(product, nomesLinhasComerciais = []) {
  const { nome, desc, linha } = getProductSnapshotForFamilyLineMatch(product);
  let fromErp = (linha || "").trim();
  if (fromErp && LINHA_SUBSTRING_DENYLIST.has(fromErp.toUpperCase())) {
    fromErp = "";
  }
  if (fromErp) return fromErp;
  const text = `${nome} ${desc}`;
  const upper = text.toUpperCase();
  for (const line of nomesLinhasComerciais) {
    if (upper.includes(String(line).toUpperCase())) return line;
  }
  return "";
}

function displayProductName(product) {
  const n = (product.Nome || "").trim();
  if (n) return n;
  const d = (product.Descricao || "").trim();
  if (d) return d;
  return String(product.IdProdutoString || "");
}

function discountPercentForItem(vitrine, item) {
  const pd = vitrine?.PercentualDesconto;
  if (pd != null && pd !== "") {
    const n = Number(pd);
    if (!Number.isNaN(n)) return n;
  }
  return Number(item.DescontoPercentual) || 0;
}

function resultToFamiliesAndRules(apiResult) {
  const vitrines = apiResult?.Vitrines;
  const produtosList = apiResult?.Produtos;
  if (!Array.isArray(vitrines) || !Array.isArray(produtosList)) {
    return {
      families: {},
      businessRules: null,
      brandLabels: {},
      brandKeys: [],
      vitrinesMeta: {},
    };
  }

  const nomesLinhasComerciais = normalizeNomesLinhasComerciais(apiResult);

  const produtosByRef = {};
  produtosList.forEach((p) => {
    if (p?.IdProdutoString != null) produtosByRef[p.IdProdutoString] = p;
  });

  const businessRules = {
    global: { minOrderValue: 0, minOrderQty: 0, minInstallmentValue: 0 },
    vitrines: {},
    categories: {},
  };

  const families = {};
  const brandLabels = {};
  const vitrinesMeta = {};

  const marcaNames = { 1: "KARSTEN", 2: "TRUSSARDI", 3: "KARSTEN DECOR" };

  vitrines.forEach((vitrine) => {
    const vitrineKey = getVitrineKey(vitrine);
    const condicoesPagamento = mapCondicoesPagamentoFromApi(
      vitrine.CondicoesPagamento,
    );
    vitrinesMeta[vitrineKey] = {
      idVitrineVirtualB2b: vitrine.IdVitrineVirtualB2b,
      idVitrineVirtualB2bCliente: vitrine.IdVitrineVirtualB2bCliente ?? null,
      nome: vitrine.Nome || "",
      vigencia: formatVitrineVigenciaLabel(vitrine),
      condicoesPagamento,
      idCondicaoPagamentoPadrao: getDefaultCondicaoPagamentoId(
        condicoesPagamento,
        vitrine.IdCondicaoPagamento,
      ),
    };
    businessRules.vitrines[vitrineKey] = {
      minValue: Number(vitrine.ValorMinimo) || 0,
      minQty: Number(vitrine.QuantidadeMinimaPedido) || 0,
      minInstallmentValue: Number(vitrine.DuplicataMinima) || 0,
      idVitrineVirtualB2b: vitrine.IdVitrineVirtualB2b,
      idVitrineVirtualB2bCliente: vitrine.IdVitrineVirtualB2bCliente ?? null,
      nome: vitrine.Nome || "",
    };

    const brandKey =
      vitrine.Marca != null
        ? MARCA_NUM_TO_BRAND_KEY[vitrine.Marca] || "outros"
        : "outros";
    if (!brandLabels[brandKey])
      brandLabels[brandKey] =
        marcaNames[vitrine.Marca] || brandKey.toUpperCase();

    const itens = vitrine.Itens || [];
    itens.forEach((item) => {
      const product = produtosByRef[item.IdProdutoString];
      if (!product) return;

      const baseFamilyKey = resolveFamilyKeyFromApiProduct(
        product,
        item.IdProdutoString,
      );
      const familyKey =
        typeof baseFamilyKey === "string" &&
        baseFamilyKey.startsWith("TOWEL_MULTI_")
          ? `${vitrineKey}::${baseFamilyKey}`
          : baseFamilyKey;
      if (!families[brandKey]) families[brandKey] = {};
      const byFamily = families[brandKey];
      if (!byFamily[familyKey]) {
        const mergedColors = mergeFotosPorCor([], product);
        const colecaoSerie = getColecaoSerieFromSpecs(product);
        const commercialLineName = extractCommercialLineFromProduct(
          product,
          nomesLinhasComerciais,
        );
        byFamily[familyKey] = {
          collectionName: (product.Colecao || "").trim(),
          nomeColecao: (product.NomeColecao || "").trim(),
          colecaoSerie,
          lineName: (product.Linha || "").trim(),
          commercialLineName,
          name:
            (product.Colecao || "").trim() ||
            (product.NomeComercial || "").trim() ||
            "Produtos",
          commercialName: (product.NomeComercial || "").trim(),
          productDisplayName: "",
          vitrine: vitrineKey,
          vitrineDisplayName: (vitrine.Nome || "").trim(),
          vitrineVigencia: formatVitrineVigenciaLabel(vitrine),
          condicoesPagamento,
          idCondicaoPagamentoPadrao: getDefaultCondicaoPagamentoId(
            condicoesPagamento,
            vitrine.IdCondicaoPagamento,
          ),
          renderType: "card",
          status: product.Status || "A",
          category: product.Categoria || "",
          subcategory: product.Subcategoria || "",
          products: [],
          fotosPorCorMerged: mergedColors,
          variants: [],
          variantImages: {},
          matrixMode: "legacy",
          matrixColors: [],
          matrixSizes: [],
          productByColorSize: {},
        };
        rebuildFamilyColorFields(byFamily[familyKey]);
      } else {
        byFamily[familyKey].fotosPorCorMerged = mergeFotosPorCor(
          byFamily[familyKey].fotosPorCorMerged,
          product,
        );
        rebuildFamilyColorFields(byFamily[familyKey]);
      }

      const productId = `${item.IdVitrineVirtualB2bItem}_${item.IdProdutoString}`;
      const corLabel = getCorFromApiProduct(product);
      const tamanhoLabel = getSizeLabelFromProduct(product);
      const displayTitle = deriveDisplayProductTitleFromApiProduct(product);
      const defaultCondId = getDefaultCondicaoPagamentoId(
        condicoesPagamento,
        vitrine.IdCondicaoPagamento,
      );
      const baseDiscount = discountPercentForItem(vitrine, item);
      byFamily[familyKey].products.push({
        id: productId,
        idProduto: Number(product.IdProduto) || product.IdProdutoString,
        name: displayTitle,
        fullName: displayProductName(product),
        displayTitle,
        corLabel,
        corHex: (product.CorHex || "").trim() || null,
        tamanhoLabel,
        ref: product.IdProdutoString || "",
        price: Number(item.Valor) || 0,
        discount: getDiscountPercentFromCondicao(
          findCondicaoPagamentoById(condicoesPagamento, defaultCondId),
          baseDiscount,
        ),
        multiple: 1,
        images: product.Fotos || [],
        especificacoes: product.Especificacoes || [],
        vitrine: vitrineKey,
        idVitrineVirtualB2b: vitrine.IdVitrineVirtualB2b,
        idVitrineVirtualB2bCliente: vitrine.IdVitrineVirtualB2bCliente ?? null,
        vitrineName: vitrine.Nome || "",
        categoriaApi: (product.Categoria || "").trim(),
        subcategoriaApi: (product.Subcategoria || "").trim(),
        segmentoToalha: (
          product.SegmentoToalha ??
          product.segmentoToalha ??
          ""
        ).trim(),
        tituloFamiliaCardPdp: (
          product.TituloFamiliaCardPdp ??
          product.tituloFamiliaCardPdp ??
          ""
        ).trim(),
        subtituloCardPdp: (
          product.SubtituloCardPdp ??
          product.subtituloCardPdp ??
          ""
        ).trim(),
      });
    });
  });

  Object.keys(families).forEach((bk) => {
    Object.keys(families[bk]).forEach((fk) => {
      const fam = families[bk][fk];
      delete fam.fotosPorCorMerged;
      finalizeFamilyRecord(fam);
    });
  });

  const brandKeys = Object.keys(families);
  brandKeys.forEach((k) => {
    if (!brandLabels[k]) brandLabels[k] = k.toUpperCase();
  });

  return { families, businessRules, brandLabels, brandKeys, vitrinesMeta };
}

const CART_STORAGE_KEY = "vitrine-empresa-cart";

function isTokenExpiredOrMissing() {
  try {
    const session = JSON.parse(
      localStorage.getItem("ApiCredentials") || "null",
    );
    if (!session?.accessToken || !session?.dtExpiration) return true;
    const dtExp = new Date(session.dtExpiration).getTime();
    const dtNow = new Date().getTime();
    return dtNow >= dtExp;
  } catch {
    return true;
  }
}

const defaultBusinessRules = {
  global: { minOrderValue: 0, minOrderQty: 0, minInstallmentValue: 0 },
  vitrines: {},
  categories: {},
};

function normalizeBrandCart(stored) {
  if (!stored || typeof stored !== "object") {
    return { order: {}, paymentConditions: {} };
  }
  if ("order" in stored || "paymentConditions" in stored) {
    return {
      order:
        stored.order && typeof stored.order === "object" ? stored.order : {},
      paymentConditions:
        stored.paymentConditions && typeof stored.paymentConditions === "object"
          ? stored.paymentConditions
          : {},
    };
  }
  return { order: stored, paymentConditions: {} };
}

function brandCartHasItems(cart) {
  const normalized = normalizeBrandCart(cart);
  return Object.keys(normalized.order).length > 0;
}

function getCartFromStorage(brandKeys = []) {
  try {
    if (!brandKeys || brandKeys.length === 0) return {};
    const raw =
      typeof window !== "undefined"
        ? localStorage.getItem(CART_STORAGE_KEY)
        : null;
    if (!raw) {
      const result = {};
      brandKeys.forEach((b) => {
        result[b] = { order: {}, paymentConditions: {} };
      });
      return result;
    }
    const parsed = JSON.parse(raw);
    const result = {};
    brandKeys.forEach((b) => {
      result[b] = normalizeBrandCart(
        parsed[b] && typeof parsed[b] === "object" ? parsed[b] : {},
      );
    });
    return result;
  } catch {
    const result = {};
    (brandKeys || []).forEach((b) => {
      result[b] = { order: {}, paymentConditions: {} };
    });
    return result;
  }
}

function getCartForBrand(brand, brandKeys = []) {
  const all = getCartFromStorage(brandKeys);
  return normalizeBrandCart(all[brand]);
}

function saveCartForBrand(brand, order, paymentConditions, brandKeys = []) {
  try {
    const all = getCartFromStorage(brandKeys);
    all[brand] = {
      order: order && typeof order === "object" ? order : {},
      paymentConditions:
        paymentConditions && typeof paymentConditions === "object"
          ? paymentConditions
          : {},
    };
    if (typeof window !== "undefined") {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(all));
    }
  } catch (e) {
    console.error("Erro ao salvar carrinho no storage:", e);
  }
}

function resolveOrderLineFromKey(key, families) {
  for (const brandFamilies of Object.values(families)) {
    if (!brandFamilies || typeof brandFamilies !== "object") continue;
    for (const [famKey, fam] of Object.entries(brandFamilies)) {
      if (!fam?.products) continue;
      for (const prod of fam.products) {
        const baseKey = `${famKey}_${prod.id}`;
        const legacyPrefix = `${baseKey}_`;
        if (key === baseKey || key.startsWith(legacyPrefix)) {
          return {
            famKey,
            fam,
            prod,
            variant:
              key === baseKey ? "" : key.substring(legacyPrefix.length),
          };
        }
      }
    }
  }
  return null;
}

export default function VitrineEmpresaPage() {
  const [loading, setLoading] = useState(true);
  const [vitrineApiResult, setVitrineApiResult] = useState(null);

  useEffect(() => {
    if (isTokenExpiredOrMissing()) {
      localStorage.setItem("sessionExpired", "true");
      UtilsFncs.RedirectToLoginPage();
    }
  }, []);

  const [showIntroScreen, setShowIntroScreen] = useState(true);
  const [currentBrand, setCurrentBrand] = useState("");
  const theme =
    themeColorsDesingSystem.brands[
      currentBrand === "karsten_decor" ? "karsten" : currentBrand
    ] || themeColorsDesingSystem.brands.karsten;
  const [orderState, setOrderState] = useState({});
  const [paymentConditionState, setPaymentConditionState] = useState({});
  const [isCartGrouped, setIsCartGrouped] = useState(false);
  const [activeFamilyKey, setActiveFamilyKey] = useState(null);
  const [tempGridState, setTempGridState] = useState({});
  const [tempPaymentConditionState, setTempPaymentConditionState] = useState({});
  const [selectedBulkColors, setSelectedBulkColors] = useState(new Set());
  const [selectedBulkProducts, setSelectedBulkProducts] = useState(new Set());
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [selectedCommercialNames, setSelectedCommercialNames] = useState(
    new Set(),
  );
  const [selectedVitrines, setSelectedVitrines] = useState(new Set());
  const [selectedSizes, setSelectedSizes] = useState(new Set());
  const [selectedColors, setSelectedColors] = useState(new Set());
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [gridModalOpen, setGridModalOpen] = useState(false);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [ordersModalOpen, setOrdersModalOpen] = useState(false);
  const [pedidosList, setPedidosList] = useState([]);
  const [loadingPedidos, setLoadingPedidos] = useState(false);
  const [submitOrderLoading, setSubmitOrderLoading] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [confirmData, setConfirmData] = useState({
    title: "",
    message: "",
    callback: null,
  });
  const [alertType, setAlertType] = useState("success");

  const derived = useMemo(() => {
    if (!vitrineApiResult) return null;
    return resultToFamiliesAndRules(vitrineApiResult);
  }, [vitrineApiResult]);

  const effectiveFamilies = derived?.families ?? {};
  const effectiveBusinessRules = derived?.businessRules ?? defaultBusinessRules;
  const effectiveBrandKeys = derived?.brandKeys ?? [];
  const effectiveBrandLabels = derived?.brandLabels ?? {};
  const vitrinesMeta = derived?.vitrinesMeta ?? {};

  const primaryVitrine = useMemo(() => {
    const vitrines = vitrineApiResult?.Vitrines;
    if (!Array.isArray(vitrines) || vitrines.length === 0) return null;
    const comCliente = vitrines.find(
      (v) => v.IdVitrineVirtualB2bCliente != null,
    );
    return comCliente ?? vitrines[0];
  }, [vitrineApiResult]);

  const brandKeysWithProducts = useMemo(() => {
    const set = new Set();
    Object.entries(effectiveFamilies).forEach(([brandKey, familiesObj]) => {
      const total = Object.values(familiesObj || {}).reduce(
        (acc, fam) => acc + (fam.products?.length || 0),
        0,
      );
      if (total > 0) set.add(brandKey);
    });
    return set;
  }, [effectiveFamilies]);

  useEffect(() => {
    async function fetchVitrines() {
      try {
        const response = await Api.get(
          "virtual-showcase-b2b-list-pdp",
          "showcaseB2B",
          "",
          true,
        );

        if (!response) {
          setVitrineApiResult(null);
          return;
        }

        if (response.Success === false) {
          setVitrineApiResult(null);
          const msg =
            response.ErrorMessage ||
            response.Message ||
            response.ValidationMessage ||
            "Não foi possível carregar as vitrines.";
          setAlertType("error");
          setAlertTitle("Atenção");
          setAlertMessage(msg);
          setAlertModalOpen(true);
          return;
        }

        const vitrines = response.Result?.Vitrines;
        const produtos = response.Result?.Produtos;
        if (vitrines && vitrines.length > 0) {
          const nomesLinhas =
            response.Result?.NomesLinhasComerciais ??
            response.Result?.nomesLinhasComerciais ??
            [];
          setVitrineApiResult({
            Vitrines: vitrines,
            Produtos: Array.isArray(produtos) ? produtos : [],
            NomesLinhasComerciais: Array.isArray(nomesLinhas)
              ? nomesLinhas
              : [],
          });
        } else {
          setVitrineApiResult(null);
          setAlertType("error");
          setAlertTitle("Atenção");
          setAlertMessage("Você não possui vitrines disponíveis.");
          setAlertModalOpen(true);
        }
      } catch {
        setVitrineApiResult(null);
      } finally {
        setLoading(false);
      }
    }

    fetchVitrines();
  }, []);

  useEffect(() => {
    if (!vitrineApiResult || effectiveBrandKeys.length === 0) return;
    const all = getCartFromStorage(effectiveBrandKeys);
    const brandWithCart = effectiveBrandKeys.find((b) =>
      brandCartHasItems(all[b]),
    );
    if (brandWithCart) {
      setCurrentBrand(brandWithCart);
      const cart = getCartForBrand(brandWithCart, effectiveBrandKeys);
      setOrderState(cart.order);
      setPaymentConditionState(cart.paymentConditions);
      setShowIntroScreen(false);
    } else {
      setShowIntroScreen(true);
    }
  }, [vitrineApiResult, effectiveBrandKeys]);

  useEffect(() => {
    if (
      !showIntroScreen &&
      primaryVitrine &&
      effectiveBrandKeys.length > 0 &&
      !effectiveBrandKeys.includes(currentBrand)
    ) {
      const brandKeyFromMarca =
        primaryVitrine.Marca != null
          ? MARCA_NUM_TO_BRAND_KEY[primaryVitrine.Marca]
          : null;
      setCurrentBrand(
        brandKeyFromMarca && effectiveBrandKeys.includes(brandKeyFromMarca)
          ? brandKeyFromMarca
          : effectiveBrandKeys[0],
      );
    }
  }, [showIntroScreen, primaryVitrine, effectiveBrandKeys, currentBrand]);

  useEffect(() => {
    if (!ordersModalOpen || !primaryVitrine?.IdVitrineVirtualB2b) {
      if (!ordersModalOpen) return;
      setPedidosList([]);
      return;
    }
    const idVitrineVirtualB2b = primaryVitrine.IdVitrineVirtualB2b;
    const idMarca =
      currentBrand === "karsten"
        ? 1
        : currentBrand === "trussardi"
          ? 2
          : currentBrand === "karsten_decor"
            ? 3
            : null;
    let cancelled = false;
    setLoadingPedidos(true);
    async function fetchPedidos() {
      try {
        const params = new URLSearchParams({
          idVitrineVirtualB2b: idVitrineVirtualB2b,
        });
        if (idMarca !== "" && idMarca != null)
          params.set("idMarca", String(idMarca));
        const response = await Api.get(
          "virtual-showcase-b2b-list-pedidos",
          "showcaseB2B",
          `?${params.toString()}`,
          true,
        );
        if (cancelled) return;
        if (response?.Success && response?.Result?.Pedidos) {
          setPedidosList(response.Result.Pedidos);
        } else {
          setPedidosList([]);
        }
      } catch {
        if (!cancelled) setPedidosList([]);
      } finally {
        if (!cancelled) setLoadingPedidos(false);
      }
    }
    fetchPedidos();
    return () => {
      cancelled = true;
    };
  }, [
    ordersModalOpen,
    primaryVitrine?.IdVitrineVirtualB2b,
    primaryVitrine?.Marca,
  ]);

  const fetchPedidoDetalhe = async (idVitrineVirtualB2bPedido) => {
    if (!primaryVitrine?.IdVitrineVirtualB2b) return null;
    try {
      const params = new URLSearchParams({
        idVitrineVirtualB2bPedido: idVitrineVirtualB2bPedido,
        idVitrineVirtualB2b: primaryVitrine.IdVitrineVirtualB2b,
      });
      const response = await Api.get(
        "virtual-showcase-b2b-pedido-detalhe",
        "showcaseB2B",
        `?${params.toString()}`,
        true,
      );
      if (response?.Success && response?.Result) return response.Result;
      return null;
    } catch {
      return null;
    }
  };

  const toggleFilter = (type, value) => {
    let set;
    switch (type) {
      case "category":
        set = selectedCategories;
        break;
      case "commercial":
        set = selectedCommercialNames;
        break;
      case "vitrine":
        set = selectedVitrines;
        break;
      case "size":
        set = selectedSizes;
        break;
      case "color":
        set = selectedColors;
        break;
      default:
        return;
    }

    const newSet = new Set(set);
    if (newSet.has(value)) {
      newSet.delete(value);
    } else {
      newSet.add(value);
    }

    switch (type) {
      case "category":
        setSelectedCategories(newSet);
        break;
      case "commercial":
        setSelectedCommercialNames(newSet);
        break;
      case "vitrine":
        setSelectedVitrines(newSet);
        break;
      case "size":
        setSelectedSizes(newSet);
        break;
      case "color":
        setSelectedColors(newSet);
        break;
    }
  };

  const openGridModal = (familyKey) => {
    setActiveFamilyKey(familyKey);
    setTempGridState({ ...orderState });
    setTempPaymentConditionState({ ...paymentConditionState });
    setSelectedBulkColors(new Set());
    setSelectedBulkProducts(new Set());
    setGridModalOpen(true);
  };

  const closeGridModal = () => {
    setGridModalOpen(false);
    setActiveFamilyKey(null);
    setTempGridState({});
    setTempPaymentConditionState({});
  };

  const saveGridSelection = (gridState, paymentState) => {
    const nextOrder = { ...(gridState ?? tempGridState) };
    setOrderState(nextOrder);
    setPaymentConditionState((prev) => {
      const merged = { ...prev, ...(paymentState ?? {}) };
      Object.keys(merged).forEach((key) => {
        if (!nextOrder[key] || Number(nextOrder[key]) <= 0) {
          delete merged[key];
        }
      });
      return merged;
    });
    closeGridModal();
  };

  const toggleBulkSelection = (type, value) => {
    if (type === "color") {
      const newSet = new Set(selectedBulkColors);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      setSelectedBulkColors(newSet);
    } else {
      const newSet = new Set(selectedBulkProducts);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      setSelectedBulkProducts(newSet);
    }
  };

  const clearOrder = () => {
    setConfirmData({
      title: "Limpar Pedido",
      message: "Tem certeza que deseja remover todos os itens do pedido?",
      callback: () => {
        setOrderState({});
        setPaymentConditionState({});
        setConfirmModalOpen(false);
      },
    });
    setConfirmModalOpen(true);
  };

  const removeFromCart = (key) => {
    setConfirmData({
      title: "Remover Item",
      message: "Deseja remover este item do pedido?",
      callback: () => {
        const newOrderState = { ...orderState };
        delete newOrderState[key];
        const newPayment = { ...paymentConditionState };
        delete newPayment[key];
        setOrderState(newOrderState);
        setPaymentConditionState(newPayment);
        setConfirmModalOpen(false);
      },
    });
    setConfirmModalOpen(true);
  };

  const [alertTitle, setAlertTitle] = useState("Pedido Enviado");
  const [alertMessage, setAlertMessage] = useState(
    "Seu pedido foi enviado com sucesso!",
  );

  const submitOrder = async () => {
    const itensPorVitrine = {};
    let missingPaymentCondition = false;

    Object.entries(orderState).forEach(([key, qty]) => {
      if (qty == null || Number(qty) <= 0) return;
      const resolved = resolveOrderLineFromKey(key, effectiveFamilies);
      if (!resolved) return;
      const { prod, fam } = resolved;
      const idVitrineVirtualB2bCliente = prod.idVitrineVirtualB2bCliente;
      if (idVitrineVirtualB2bCliente == null) return;
      const vitrineKey = prod.vitrine;
      const condicoes = fam.condicoesPagamento || [];
      const idCondicaoPagamento =
        paymentConditionState[key] ??
        getDefaultCondicaoPagamentoId(
          condicoes,
          fam.idCondicaoPagamentoPadrao,
        );

      if (condicoes.length > 0 && idCondicaoPagamento == null) {
        missingPaymentCondition = true;
        return;
      }

      const discountPercent = resolveLineDiscount(
        prod,
        fam,
        idCondicaoPagamento,
      );
      const valorUnitario = netUnitPrice(prod, discountPercent);

      if (!itensPorVitrine[vitrineKey]) {
        itensPorVitrine[vitrineKey] = {
          idVitrineVirtualB2bCliente,
          itens: [],
        };
      }
      const itemPayload = {
        idProduto: prod.idProduto ?? prod.ref,
        quantidade: Number(qty),
        valorUnitario: Math.round(valorUnitario * 100) / 100,
      };
      if (idCondicaoPagamento != null) {
        itemPayload.idCondicaoPagamento = Number(idCondicaoPagamento);
      }
      itensPorVitrine[vitrineKey].itens.push(itemPayload);
    });

    if (missingPaymentCondition) {
      setAlertType("error");
      setAlertTitle("Condição de pagamento");
      setAlertMessage(
        "Alguns itens do pedido não possuem condição de pagamento. Abra a grade e selecione a condição em cada SKU.",
      );
      setAlertModalOpen(true);
      return;
    }

    const vitrinesComItens = Object.entries(itensPorVitrine).filter(
      ([, data]) => data.itens.length > 0,
    );
    if (vitrinesComItens.length === 0) {
      setAlertType("error");
      setAlertTitle("Erro");
      setAlertMessage("Adicione itens ao pedido antes de finalizar.");
      setAlertModalOpen(true);
      return;
    }

    setSubmitOrderLoading(true);
    const resultados = [];
    let erroMsg = null;
    try {
      for (const [, data] of vitrinesComItens) {
        const response = await Api.post(
          "finalize-virtual-showcase-b2b-pedido",
          "showcaseB2B",
          {
            idVitrineVirtualB2bCliente: data.idVitrineVirtualB2bCliente,
            itens: data.itens,
          },
          true,
        );
        if (
          response &&
          response.Success &&
          response.Result?.IdVitrineVirtualB2bPedido
        ) {
          resultados.push(response.Result.IdVitrineVirtualB2bPedido);
        } else {
          erroMsg =
            response?.ErrorMessage ||
            response?.Message ||
            "Erro ao finalizar pedido.";
          break;
        }
      }

      if (erroMsg) {
        setAlertType("error");
        setAlertTitle("Erro");
        setAlertMessage(erroMsg);
        setAlertModalOpen(true);
        return;
      }
      setOrderState({});
      setPaymentConditionState({});
      setCartModalOpen(false);
      setAlertType("success");
      setAlertTitle("Pedido(s) Enviado(s)!");
      setAlertMessage(
        resultados.length > 1
          ? `Seus pedidos foram enviados com sucesso! Pedidos: #${resultados.join(", #")}`
          : `Seu pedido foi enviado com sucesso! Pedido: #${resultados[0]}`,
      );
      setAlertModalOpen(true);
    } catch (error) {
      const msg =
        typeof error === "string"
          ? error
          : error?.message || "Erro ao finalizar pedido. Tente novamente.";
      setAlertType("error");
      setAlertTitle("Erro");
      setAlertMessage(msg);
      setAlertModalOpen(true);
    } finally {
      setSubmitOrderLoading(false);
    }
  };

  const filteredFamilies = useMemo(() => {
    const brandFamilies = effectiveFamilies[currentBrand];
    return Object.keys(brandFamilies || {}).filter((key) => {
      const fam = brandFamilies[key];

      const catMatch =
        selectedCategories.size === 0 ||
        selectedCategories.has(String(fam.category || "").trim());

      const commMatch =
        selectedCommercialNames.size === 0 ||
        selectedCommercialNames.has(String(fam.commercialName || "").trim());

      const vitrineMatch =
        selectedVitrines.size === 0 ||
        selectedVitrines.has(fam.vitrine) ||
        (fam.products || []).some((p) => selectedVitrines.has(p.vitrine));

      const sizesInFam = new Set();
      (fam.matrixSizes || []).forEach((s) => {
        const t = String(s || "").trim();
        if (t) sizesInFam.add(t);
      });
      (fam.products || []).forEach((p) => {
        const t = String(p.tamanhoLabel || "").trim();
        if (t) sizesInFam.add(t);
      });
      const sizeMatch =
        selectedSizes.size === 0 ||
        [...sizesInFam].some((s) => selectedSizes.has(s));

      const colorsInFam = new Set();
      (fam.matrixColors || []).forEach((c) => {
        const t = String(c || "").trim();
        if (t && t !== "Geral") colorsInFam.add(t);
      });
      (fam.variants || []).forEach((v) => {
        const t = String(v || "").trim();
        if (t && t !== "Geral") colorsInFam.add(t);
      });
      (fam.products || []).forEach((p) => {
        const t = String(p.corLabel || "").trim();
        if (t && t !== "Geral") colorsInFam.add(t);
      });
      const colorMatch =
        selectedColors.size === 0 ||
        [...colorsInFam].some((c) => selectedColors.has(c));

      return catMatch && commMatch && vitrineMatch && sizeMatch && colorMatch;
    });
  }, [
    currentBrand,
    effectiveFamilies,
    selectedCategories,
    selectedCommercialNames,
    selectedVitrines,
    selectedSizes,
    selectedColors,
  ]);

  const totals = useMemo(() => {
    let qty = 0;
    let val = 0;
    const brandFamilies = effectiveFamilies[currentBrand];

    Object.entries(orderState).forEach(([key, quantity]) => {
      const resolved = resolveOrderLineFromKey(key, { [currentBrand]: brandFamilies });
      if (!resolved) return;
      const { prod, fam } = resolved;
      const idCond =
        paymentConditionState[key] ??
        getDefaultCondicaoPagamentoId(
          fam.condicoesPagamento,
          fam.idCondicaoPagamentoPadrao,
        );
      const price = netUnitPrice(
        prod,
        resolveLineDiscount(prod, fam, idCond),
      );
      qty += Number(quantity) || 0;
      val += (Number(quantity) || 0) * price;
    });

    return { qty, val };
  }, [orderState, paymentConditionState, currentBrand, effectiveFamilies]);

  useEffect(() => {
    if (effectiveBrandKeys.length === 0) return;
    saveCartForBrand(
      currentBrand,
      orderState,
      paymentConditionState,
      effectiveBrandKeys,
    );
  }, [currentBrand, orderState, paymentConditionState, effectiveBrandKeys]);

  const handleRequestShowIntro = () => {
    if (Object.keys(orderState).length > 0) {
      setConfirmData({
        title: "Trocar de marca",
        message: "Deseja trocar de marca? O carrinho atual será limpo.",
        callback: () => {
          saveCartForBrand(currentBrand, {}, {}, effectiveBrandKeys);
          setOrderState({});
          setPaymentConditionState({});
          setShowIntroScreen(true);
          setConfirmModalOpen(false);
        },
      });
      setConfirmModalOpen(true);
    } else {
      setShowIntroScreen(true);
    }
  };

  const handleSelectBrand = (name) => {
    saveCartForBrand(
      currentBrand,
      orderState,
      paymentConditionState,
      effectiveBrandKeys,
    );
    setCurrentBrand(name);
    const cart = getCartForBrand(name, effectiveBrandKeys);
    setOrderState(cart.order);
    setPaymentConditionState(cart.paymentConditions);
    setShowIntroScreen(false);
  };

  if (loading) {
    return (
      <Box
        className="fazer-pedido-page vitrine-empresa-loading"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress size={48} />
        <Typography style={{ marginTop: 16 }}>Carregando vitrine...</Typography>
      </Box>
    );
  }

  return (
    <Box
      className={`theme-${currentBrand === "karsten_decor" ? "karsten" : currentBrand} fazer-pedido-page`}
    >
      {showIntroScreen && (
        <IntroBrandScreen
          onSelectBrand={handleSelectBrand}
          brandKeysWithProducts={brandKeysWithProducts}
        />
      )}
      {!showIntroScreen && primaryVitrine && (
        <>
          <Header
            selectedBrand={currentBrand}
            theme={theme}
            onShowIntroScreen={handleRequestShowIntro}
            currentBrandLabel={
              effectiveBrandLabels[currentBrand] || currentBrand
            }
          />
          <Box className="main-container">
            <Sidebar
              currentBrand={currentBrand}
              families={effectiveFamilies}
              selectedCategories={selectedCategories}
              selectedCommercialNames={selectedCommercialNames}
              selectedVitrines={selectedVitrines}
              selectedSizes={selectedSizes}
              selectedColors={selectedColors}
              productSearchTerm={productSearchTerm}
              setProductSearchTerm={setProductSearchTerm}
              toggleFilter={toggleFilter}
              theme={theme}
              vitrinesMeta={vitrinesMeta}
            />
            <Box className="main-content">
              <ProductList
                currentBrand={currentBrand}
                families={effectiveFamilies}
                filteredFamilies={filteredFamilies}
                orderState={orderState}
                setOrderState={setOrderState}
                openGridModal={openGridModal}
                productSearchTerm={productSearchTerm}
                theme={theme}
              />
            </Box>
          </Box>
          <Footer
            totals={totals}
            clearOrder={clearOrder}
            openCartModal={() => setCartModalOpen(true)}
            openOrdersModal={() => setOrdersModalOpen(true)}
            theme={theme}
          />
          <GridModal
            open={gridModalOpen}
            onClose={closeGridModal}
            onSave={saveGridSelection}
            currentBrand={currentBrand}
            activeFamilyKey={activeFamilyKey}
            families={effectiveFamilies}
            tempGridState={tempGridState}
            setTempGridState={setTempGridState}
            tempPaymentConditionState={tempPaymentConditionState}
            setTempPaymentConditionState={setTempPaymentConditionState}
            selectedBulkColors={selectedBulkColors}
            selectedBulkProducts={selectedBulkProducts}
            toggleBulkSelection={toggleBulkSelection}
            theme={theme}
          />
          <CartModal
            open={cartModalOpen}
            onClose={() => setCartModalOpen(false)}
            currentBrand={currentBrand}
            families={effectiveFamilies}
            orderState={orderState}
            paymentConditionState={paymentConditionState}
            isCartGrouped={isCartGrouped}
            toggleCartGrouping={() => setIsCartGrouped(!isCartGrouped)}
            removeFromCart={removeFromCart}
            submitOrder={submitOrder}
            submitOrderLoading={submitOrderLoading}
            businessRules={effectiveBusinessRules}
            theme={theme}
            vitrinesMeta={vitrinesMeta}
          />
          <OrdersModal
            open={ordersModalOpen}
            onClose={() => setOrdersModalOpen(false)}
            pedidos={pedidosList}
            loading={loadingPedidos}
            fetchPedidoDetalhe={fetchPedidoDetalhe}
            theme={theme}
          />
          <ConfirmModal
            open={confirmModalOpen}
            onClose={() => setConfirmModalOpen(false)}
            onConfirm={() => {
              if (confirmData.callback) confirmData.callback();
            }}
            title={confirmData.title}
            message={confirmData.message}
            theme={theme}
          />
        </>
      )}
      <AlertModal
        open={alertModalOpen}
        onClose={() => setAlertModalOpen(false)}
        title={alertTitle}
        message={alertMessage}
        theme={theme}
        type={alertType}
      />
    </Box>
  );
}
